import ApiService from '../../../../services/apiService';
import MapService from '../../../../services/mapService';
import WGS84Service from '../../../../services/wgs84ProjectionService';
import { mapState } from 'vuex';
import { randomInt } from '../../../../utils/vuex';
import uuidv4 from 'uuid/v4';
import debounce from 'lodash/debounce';
import get from 'lodash/get';
import compact from 'lodash/compact';
import round from 'lodash/round';

const kDtwinExtrusionPolygonId = 'dtwin_extrusion_polygon';
// contains the ground-overlay image
const kLayerZIndexBase = 'z-index-base';
const kLayerZIndex1 = 'z-index-1';
const kLayerZIndex2 = 'z-index-2';

export default {
  props: {
    cameraObj: {
      type: Object,
      required: true,
    },
    mapBoundsLock: {
      type: Array,
      required: false,
      default: () => [],
    },
    animateFlyIn: {
      type: Boolean,
      required: false,
      default: () => true,
    },
    markerItems: {
      type: Array,
      required: true,
    },
    overlayInfo: {
      type: Object,
      required: false,
      default: null,
    },
    clusters: {
      type: Array,
      required: true,
    },
    polygons: {
      type: Array,
      required: true,
    },
    hideStatelessPolygons: {
      type: Boolean,
      required: false,
    },
    hideStateHistoryBefore: {
      type: String,
      required: false,
    },
    gradientPolygonColours: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    selectedCluster: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    detailsGetApiMethod: {
      type: String,
      required: false,
      default: 'digitalTwinGet',
    },
  },
  data() {
    return {
      realCamera: {
        center: [9.7027, 52.3736],
        zoom: 7,
        bearing: randomInt(90) - 90,
      },

      polygonSourcesLayerIds: [],
      clusterRefreshInterval: null,

      // should the map zoom to the polygon of the twin on click?
      // (public map should stay put instead)
      zoomToTwins: true,
      // should the polygon-width be scrolled to the left, so the default-detail-view has space?
      // disable if the marker should stay centered (e.g. in the public map)
      // has no influence if `zoomToTwins` is false
      scrollByPolygonOnZoom: true,
      // should the twin geo-polygons be drawn (and extruded on select)?
      displayTwinPolygons: true,

      // anytime the map-bounds is refreshed (a different floor/building) is displayed,
      // force a new map-instance to be created; because MapGL does not seem to like changing the bounds on runtime
      mapBoundsLockKey: 1,
      // setting this flag will set `this.map` to null if mapBoundsLockKey is changed
      // otherwise certain methods will cause "NullPointerExceptions"
      resetMapOnBoundsChange: false,

      // can also be overwritten by route query param `pitch`
      defaultPitch: 15,
      // can also be overwritten by route query param `bearing`
      defaultBearing: 0,

      markers: [],
      polygonValues: [],
      light: 'mapbox://styles/mapbox/light-v9',
      dark: 'mapbox://styles/mapbox/dark-v10',
      dmStreets: 'mapbox://styles/digimondo/cjn4e7kzsjv8w2rlc0a574xu4',
      satellite: 'mapbox://styles/mapbox/satellite-v9',
      streets: 'mapbox://styles/mapbox/streets-v11',

      hasLoadedMarkers: false,
      lastBoundsRefresh: { _ne: { lng: 0, lat: 0 }, _sw: { lng: 0, lat: 0 } },
      selectedMarkerItem: {},

      // any animations in the map are overwritten if a new one starts (`flyTo`)
      // as they return a promise, keep this queue going to start one animation after another
      animationQueue: [],
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color']),

    markerColor() {
      return MapService.markerColor(this.color, this.$store);
    },
    mapAccessToken() {
      return MapService.getAccessToken(this.$store.state.app.UiTheme ? 'light' : 'dark');
    },
  },
  watch: {
    markerItems() {
      if (this.hasLoadedMarkers) {
        return;
      }
      if (this.map && (this.markerItems.length > 0 || this.clusters.length > 0)) {
        this.hasLoadedMarkers = true;
        this.flyToCamera();
      }
    },
    mapBoundsLock() {
      if (this.resetMapOnBoundsChange) {
        this.mapBoundsLockKey++;
        this.map = null;
      }
    },
    overlayInfo() {
      this.drawOverlay();
    },
    clusters() {
      if (this.hasLoadedMarkers) {
        return;
      }
      if (this.map && (this.markerItems.length > 0 || this.clusters.length > 0)) {
        this.hasLoadedMarkers = true;
        this.flyToCamera();
      }
    },
    polygons() {
      this.addPolygons();
    },
    mapStyle() {
      // if the map-style changes, need to reload the map, in order for the polygons to render again
      this.addZIndexHelperLayers();
      this.addPolygons();
    },
    gradientPolygonColours() {
      this.addPolygons();
    },
    displayTwinPolygons() {
      if (this.map) {
        this.addPolygons();
        if (!this.displayTwinPolygons) {
          // remove the extruding polygon of a selected twin
          if (this.map.getLayer(kDtwinExtrusionPolygonId)) {
            this.map.removeLayer(kDtwinExtrusionPolygonId);
          }
          if (this.map.getSource(kDtwinExtrusionPolygonId)) {
            this.map.removeSource(kDtwinExtrusionPolygonId);
          }
        }
        // TODO: what about the reverse case, when a twin is selected and polygons are toggled?
      }
    },
    $route() {
      this.defaultPitch = this.getIntFromQueryParam('pitch', this.defaultPitch);
      this.defaultBearing = this.getIntFromQueryParam('bearing', this.defaultBearing);
    },
  },
  created() {
    this.map = null;
  },
  mounted() {
    this.defaultPitch = this.getIntFromQueryParam('pitch', this.defaultPitch);
    this.defaultBearing = this.getIntFromQueryParam('bearing', this.defaultBearing);
  },
  methods: {
    getIntFromQueryParam(paramName, defaultValue) {
      const paramValue = parseInt(get(this.$route, `query.${paramName}`));
      if (isNaN(paramValue) || paramValue < 0) {
        return defaultValue;
      }
      return paramValue;
    },
    onMapLoaded(event) {
      this.map = get(this, '$refs.twinMap.map', event.map);

      // Add 3D Buildings in case switched on in filter
      if (this.$store.state.app.map3DBuild) {
        MapService.add3DBuildingLayer(this.map);
      }

      this.addZIndexHelperLayers();
      this.addPolygons();

      this.$emit('mapLoad', event.map);
    },
    closeDtwinPopup() {
      this.selectedMarkerItem.id = null;
      if (this.map) {
        if (this.map.getLayer(kDtwinExtrusionPolygonId)) {
          this.map.removeLayer(kDtwinExtrusionPolygonId);
        }
        if (this.map.getSource(kDtwinExtrusionPolygonId)) {
          this.map.removeSource(kDtwinExtrusionPolygonId);
        }
      }
    },
    cancelDtwin() {
      this.selectedTwin.id = null;
    },
    addToAnimationQueue(params) {
      this.animationQueue.push(params);
      this.processAnimationQueue();
    },
    processAnimationQueue() {
      if (this.animationQueue.length > 0) {
        if (this.map && !this.map.isMoving()) {
          const firstEntry = this.animationQueue[0];
          this.animationQueue.shift();
          this.map.flyTo(firstEntry);
        } else {
          setTimeout(() => {
            this.processAnimationQueue();
          }, 100);
        }
      }
    },

    flyToCamera: debounce(function () {
      setTimeout(() => {
        // if (this.map) {
        //   this.map.flyTo({
        //     center: this.cameraObj.center,
        //     pitch: this.defaultPitch,
        //     zoom: this.cameraObj.zoom > 0 ? this.cameraObj.zoom : 7,
        //     bearing: 0,
        //     duration: this.animateFlyIn ? 2000 : 0,
        //     animate: this.animateFlyIn,
        //   });
        // }
        this.addToAnimationQueue({
          center: this.cameraObj.center,
          pitch: this.defaultPitch,
          zoom: this.cameraObj.zoom > 0 ? this.cameraObj.zoom : 7,
          bearing: this.defaultBearing,
          duration: this.animateFlyIn ? 2000 : 0,
          animate: this.animateFlyIn,
        });
      }, 100);
    }, 100),

    addZIndexHelperLayers() {
      if (this.map) {
        if (this.map.getLayer(kLayerZIndexBase)) {
          this.map.removeLayer(kLayerZIndexBase);
        }
        if (this.map.getLayer(kLayerZIndex1)) {
          this.map.removeLayer(kLayerZIndex1);
        }
        if (this.map.getLayer(kLayerZIndex2)) {
          this.map.removeLayer(kLayerZIndex2);
        }
        if (this.map.getSource('empty')) {
          this.map.removeSource('empty');
        }

        // since there is no direct z-index property, create dummy layers under which stuff is positioned
        this.map.addSource('empty', {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] },
        });
        this.map.addLayer({
          id: kLayerZIndex2,
          type: 'symbol',
          source: 'empty',
        });
        this.map.addLayer(
          {
            id: kLayerZIndex1,
            type: 'symbol',
            source: 'empty',
          },
          kLayerZIndex2
        ); // place this layer below zIndex2
        this.map.addLayer(
          {
            id: kLayerZIndexBase,
            type: 'symbol',
            source: 'empty',
          },
          kLayerZIndex1
        ); // place this layer below zIndex1
      }
    },

    drawOverlay() {
      if (!this.map) {
        setTimeout(() => {
          this.drawOverlay();
        }, 100);
        return;
      }
      const overlayId = 'overlay';
      if (this.map.getLayer(overlayId)) {
        this.map.removeLayer(overlayId);
      }
      if (this.map.getSource(overlayId)) {
        this.map.removeSource(overlayId);
      }
      if (!this.overlayInfo) {
        return;
      }
      this.map.addSource(overlayId, {
        type: 'image',
        url: this.overlayInfo.image,
        coordinates: this.overlayInfo.coordinates,
      });
      this.map.addLayer(
        {
          id: overlayId,
          source: overlayId,
          type: 'raster',
          paint: { 'raster-opacity': 1.0 },
        },
        kLayerZIndexBase
      );
    },

    addPolygons: debounce(function () {
      if (!this.map) {
        return;
      }
      this.polygonSourcesLayerIds.forEach((id) => {
        if (this.map.getLayer(id)) {
          this.map.removeLayer(id);
        }
        if (this.map.getSource(id)) {
          this.map.removeSource(id);
        }
      });
      this.polygonSourcesLayerIds = [];

      if (this.displayTwinPolygons) {
        let plainItems = this.polygons.filter((item) => {
          return !item.stateVisual || item.stateValue === undefined || item.stateValue === null;
        });
        let greyedItems = this.polygons.filter((item) => {
          return (
            item.stateVisual &&
            item.stateValue !== undefined &&
            item.stateValue !== null &&
            new Date(item.stateTime).getTime() < new Date(this.hideStateHistoryBefore).getTime()
          );
        });
        let colouredItems = this.polygons.filter((item) => {
          return (
            item.stateVisual &&
            item.stateValue !== undefined &&
            item.stateValue !== null &&
            new Date(item.stateTime).getTime() >= new Date(this.hideStateHistoryBefore).getTime()
          );
        });

        const hasPickedStateValues = greyedItems.length > 0 || colouredItems.length > 0;

        // if a state-value was picked (even if greyed out), show the default-polygons slightly more transparent,
        // to make it clear they are not belonging to a coloured picked state
        let defaultColor = MapService.convertToRgba(this.markerColor, hasPickedStateValues ? 0.04 : 0.25);
        let defaultOutlineColor = MapService.convertToRgba(this.markerColor, hasPickedStateValues ? 0.4 : 1);

        const allPolygons = colouredItems
          .map((item) => {
            const { color, outlineColor, displayIcon } = this.colorForPolygon(item);
            return {
              ...item,
              color,
              outlineColor,
              displayIcon,
              polygons: [item.polygon],
              value: item.stateValue,
            };
          })
          .concat(
            greyedItems.map((item) => {
              const { displayIcon } = this.colorForPolygon(item);
              return {
                ...item,
                color: 'rgba(100, 100, 100, 0.3)',
                outlineColor: 'rgba(100, 100, 100, 0.8)',
                transparent: true,
                displayIcon,
                polygons: [item.polygon],
                value: item.stateValue,
              };
            })
          )
          .concat([
            {
              color: defaultColor,
              outlineColor: defaultOutlineColor,
              // do not show polygons without state-value/visualisation
              polygons: this.hideStatelessPolygons ? [] : plainItems.map((item) => item.polygon),
            },
          ]);

        this.polygonValues = compact(
          allPolygons.map((polygonData) => {
            const polygonLayerId = uuidv4();
            let polygonGeoJson = {
              type: 'geojson',
              data: {
                type: 'FeatureCollection',
                crs: { type: 'name', properties: { name: 'urn:ogc:def:crs:OGC:1.3:CRS84' } },
                features: polygonData.polygons.map((polygon) => {
                  return {
                    type: 'Feature',
                    properties: {},
                    geometry: {
                      type: 'Polygon',
                      coordinates: polygon,
                    },
                  };
                }),
              },
            };
            this.map.addSource(polygonLayerId, polygonGeoJson);
            this.map.addLayer(
              {
                id: polygonLayerId,
                type: 'fill',
                source: polygonLayerId,
                paint: {
                  'fill-color': polygonData.color,
                  'fill-outline-color': polygonData.outlineColor,
                },
              },
              kLayerZIndex2
            );
            this.polygonSourcesLayerIds.push(polygonLayerId);

            if (polygonData.value === undefined || polygonData.value === null) {
              return null;
            }
            return {
              ...polygonData,
              coordinates: polygonData.centroid,
              color: polygonData.outlineColor,
              value: typeof polygonData.value === 'number' ? round(polygonData.value, 3) : polygonData.value,
              transparent: polygonData.transparent,
            };
          })
        );
      }
    }, 100),

    colorForPolygon(polygon) {
      // 2 cases:
      // this is either a bool-visual state with just 2 coloürs
      if (polygon.isBoolVisual) {
        const outlineColor =
          get(polygon, `stateVisual.boolVisual.${polygon.stateValue ? 'firstColor' : 'secondColor'}`) ||
          this.markerColor;
        const color = MapService.convertToRgba(outlineColor, 0.35);
        const displayIcon = get(
          polygon,
          `stateVisual.boolVisual.${polygon.stateValue ? 'firstFaIcon' : 'secondFaIcon'}`
        );
        return { outlineColor, color, displayIcon };
      }

      // or a gauge visual with a gradient of coloürs
      let values = [parseFloat(polygon.stateVisual.min)].concat(
        polygon.stateVisual.thresholds.split(',').map((t) => parseFloat(t))
      );
      let index = 0;
      if (polygon.stateValue >= values[0]) {
        for (index = 0; index < values.length; index++) {
          if (values[index] > polygon.stateValue) {
            index--;
            break;
          }
        }
      }
      index = Math.max(0, Math.min(index, values.length - 1));
      let outlineColor = polygon.stateVisual.thresColors[index];
      let color = MapService.convertToRgba(outlineColor, 0.35);

      if (index !== values.length - 1 && this.gradientPolygonColours) {
        let colors = [polygon.stateVisual.thresColors[index], polygon.stateVisual.thresColors[index + 1]];
        const min = Math.min(polygon.stateValue, values[index]);
        const mix = (polygon.stateValue - min) / (values[index + 1] - min);
        outlineColor = MapService.mixRgbaColor(colors[0], colors[1], mix);
        color = MapService.mixRgbaColor(colors[0], colors[1], mix, 0.35);
      }
      return { outlineColor, color, displayIcon: polygon.icon };
    },

    updateMarkers: debounce(function () {
      if (!this.map) {
        return;
      }
      const bounds = this.map.getBounds();
      const screenWidth = this.$refs.twinMap.$el.clientWidth;
      const boundsWidths = {
        lng: Math.abs(bounds['_sw'].lng - bounds['_ne'].lng) / screenWidth,
        lat: Math.abs(bounds['_sw'].lat - bounds['_ne'].lat) / screenWidth,
      };

      if (!get(this.lastBoundsRefresh, '_ne.lng')) {
        this.requestClusterRequest(bounds);
        return;
      }

      if (
        Object.keys(this.lastBoundsRefresh || {}).some((position) => {
          return Object.keys(get(this.lastBoundsRefresh, position, [])).some((latOrLong) => {
            // can move the map around for 5 pixels before refreshing, little performance optimisation
            // mostly only valid for the same zoom-level
            return (
              Math.abs(bounds[position][latOrLong] - get(this.lastBoundsRefresh, `${position}.${latOrLong}`)) >
              boundsWidths[latOrLong] * 5
            );
          });
        })
      ) {
        this.requestClusterRequest(bounds);
        return;
      }
    }, 333),

    debouncedRequestClusterRequest: debounce(function () {
      this.requestClusterRequest();
    }, 333),

    requestClusterRequest(bounds = undefined) {
      this.lastBoundsRefresh = bounds || this.map ? this.map.getBounds() : null;
      this.$emit('map-moved', {
        bounds: this.lastBoundsRefresh
          ? [
              [this.lastBoundsRefresh['_ne'].lng, this.lastBoundsRefresh['_ne'].lat],
              [this.lastBoundsRefresh['_sw'].lng, this.lastBoundsRefresh['_sw'].lat],
            ]
          : null,
      });

      if (this.clusterRefreshInterval) {
        clearInterval(this.clusterRefreshInterval);
      }
      this.clusterRefreshInterval = setInterval(this.requestClusterRequest, 45000);
    },

    clusterIconSize(count) {
      // parse as int again, as the clustering could return a `XXX+` if there are too many items
      const realCount = parseInt(count);
      const minMaxSize = [35, 130];
      const minMaxCount = [5, 50];

      const currCount = Math.min(Math.max(realCount, minMaxCount[0]), minMaxCount[1]);
      const gradient = (currCount - minMaxCount[0]) / (minMaxCount[1] - minMaxCount[0]);
      const bubbleSize = Math.floor(minMaxSize[0] + (minMaxSize[1] - minMaxSize[0]) * gradient);

      return `height: ${bubbleSize}px; width: ${bubbleSize}px; padding-top: ${Math.floor(0.5 * (bubbleSize - 20))}px`;
    },

    zoomIntoCluster(cluster) {
      if (!this.map) {
        return;
      }
      // max zoom level is 22, but at this point the clusters will probably not be auseinandergeriemelt any more
      if (this.map.getZoom() >= 21.6) {
        const bounds = this.map.getBounds();
        this.$emit('getClusterContent', cluster, bounds);
      }
      this.map.fitBounds(cluster.bbox);
    },

    markerItemClicked(a) {
      // cleanup map-items from prior selections
      delete this.selectedCluster.items;
      if (this.map.getLayer(kDtwinExtrusionPolygonId)) {
        this.map.removeLayer(kDtwinExtrusionPolygonId);
      }
      if (this.map.getSource(kDtwinExtrusionPolygonId)) {
        this.map.removeSource(kDtwinExtrusionPolygonId);
      }

      // preliminary show the detail-view with skeleton-loaders (no data-object)
      this.selectedMarkerItem = {
        title: a.component.$attrs.title,
        id: a.component.$attrs.dtId,
      };

      // scroll to the marker, do not change the zoom-level
      // depending on if this was a real marker or selected from a clustered-table-list, the content is a little different
      const latitude = get(a, 'marker._lngLat.lat', get(a, 'coordinates[1]'));
      const longitude = get(a, 'marker._lngLat.lng', get(a, 'coordinates[0]'));
      if (!isNaN(latitude) && !isNaN(longitude)) {
        if (this.zoomToTwins) {
          this.zoomToCoordinates([longitude, latitude]);
        }
      }

      return ApiService[this.detailsGetApiMethod](this, a.component.$attrs.dtId).then((dtwin) => {
        this.selectedMarkerItem = dtwin;

        // check if twin has defined polygon
        if (dtwin.polygon && dtwin.polygon.coordinates && dtwin.polygon.coordinates.length > 0) {
          if (!this.map) {
            return;
          }
          if (this.map.getLayer(kDtwinExtrusionPolygonId)) {
            this.map.removeLayer(kDtwinExtrusionPolygonId);
          }
          if (this.map.getSource(kDtwinExtrusionPolygonId)) {
            this.map.removeSource(kDtwinExtrusionPolygonId);
          }

          if (this.displayTwinPolygons) {
            let polygonGeoJson = {
              type: 'geojson',
              data: {
                type: 'FeatureCollection',
                crs: { type: 'name', properties: { name: 'urn:ogc:def:crs:OGC:1.3:CRS84' } },
                features: [
                  {
                    type: 'Feature',
                    properties: {},
                    geometry: {
                      type: 'Polygon',
                      coordinates: dtwin.polygon.coordinates,
                    },
                  },
                ],
              },
            };
            this.map.addSource(kDtwinExtrusionPolygonId, polygonGeoJson);

            const coloredPolygon = this.polygons.find((item) => {
              return (
                item.id === dtwin.id && item.stateVisual && item.stateValue !== undefined && item.stateValue !== null
              );
            });
            let color = MapService.markerColor('warning');
            if (coloredPolygon) {
              color = this.colorForPolygon(coloredPolygon).outlineColor;
            }

            this.map.addLayer(
              {
                id: kDtwinExtrusionPolygonId,
                type: 'fill-extrusion',
                source: kDtwinExtrusionPolygonId,
                paint: {
                  'fill-extrusion-color': color,
                  'fill-extrusion-opacity': 0.5,
                  'fill-extrusion-height': 2,
                  'fill-extrusion-base': 0,
                },
              },
              kLayerZIndex1
            );
          }
        }

        if (this.zoomToTwins) {
          this.zoomToCoordinates(get(dtwin, 'coordinates.coordinates'), dtwin.polygon);
        }
      });
    },

    zoomToCoordinates(coordinates, polygon) {
      if (!this.map) {
        return;
      }
      if (!coordinates || !Array.isArray(coordinates) || coordinates.length < 2) {
        return;
      }

      const bounds = this.map.getBounds();
      const mapDimensions = { width: this.$refs.twinMap.$el.clientWidth, height: this.$refs.twinMap.$el.clientHeight };

      // do not change the zoom-level, if there is no polygon, as there is nothing to zoom to
      let zoomLevel = undefined;
      let boundsWidth = Math.abs(bounds['_sw']['lng'] - bounds['_ne']['lng']);

      // otherwise calculate the new zoom-level relative to the polygon-dimensions
      if (polygon && polygon.coordinates && polygon.coordinates.length > 0 && this.scrollByPolygonOnZoom) {
        let minBounds = [360, 180];
        let maxBounds = [-360, -180];

        polygon.coordinates[0].forEach((c) => {
          minBounds[0] = Math.min(c[0], minBounds[0]);
          minBounds[1] = Math.min(c[1], minBounds[1]);
          maxBounds[0] = Math.max(c[0], maxBounds[0]);
          maxBounds[1] = Math.max(c[1], maxBounds[1]);
        });
        const latSpan = maxBounds[1] - minBounds[1];
        const lngSpan = maxBounds[0] - minBounds[0];
        // expand the bounds relative to the polygon-span
        const spanExpansion = 2.5;

        const northEast = {
          lon: maxBounds[0] + lngSpan * spanExpansion * 0.5,
          lat: maxBounds[1] + latSpan * spanExpansion * 0.5,
        };
        const southWest = {
          lon: minBounds[0] - lngSpan * spanExpansion * 0.5,
          lat: minBounds[1] - latSpan * spanExpansion * 0.5,
        };

        zoomLevel = WGS84Service.boundsZoomLevel({ northEast, southWest }, mapDimensions);
        // NOTE: the adaption for better UX to limit the zoom-level around the current set one is nice,
        // BUT then the `boundsWidth` also needs to re-calculated for the adapted zoom. that is out of scope for now
        // zoomLevel = Math.min(Math.max(this.map.getZoom() - 1.0, zoomLevel), this.map.getZoom() + 1.0);
        if (!isNaN(zoomLevel)) {
          boundsWidth = Math.abs(southWest.lon - northEast.lon);
        } else {
          zoomLevel = undefined;
        }
      }

      // the popup window is 600px wide, so offset half of that (if scrolling is enabled)
      const moveLng = this.scrollByPolygonOnZoom ? (boundsWidth / mapDimensions.width) * 300 : 0;
      let flyToParams = {
        center: [coordinates[0] + moveLng, coordinates[1]],
        bearing: 0,
        duration: 2000,
        animate: true,
      };
      if (zoomLevel) {
        flyToParams['zoom'] = zoomLevel;
        flyToParams['pitch'] = 25;
      }
      this.addToAnimationQueue(flyToParams);
    },

    mapMarkerIconUrl(imageId) {
      return `${process.env.VUE_APP_API_HOST}${process.env.VUE_APP_API_PATH}/upload/${imageId}`;
    },
  },
};
